// src/components/resources/calculators/CPVCalculator.tsx

'use client'

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import { FAQ } from '~/components/shared/FAQ'
import { Tooltip } from '~/components/Tooltip'
import { formatNumber } from '~/lib/utils/formatters'
import { formatNumberInput, parseInputNumber } from '~/lib/utils/format/input'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type {
  EmailTemplateConfig,
  AnalyticsConfig,
  CRMConfig
} from '~/components/forms/base/types'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import { calculateMetrics, type CalculatorInputs, type CalculatorResults } from './CPVCalculator.logic'

interface NumberFormatOptions {
  decimals?: number
  unit?: string
  unitPosition?: 'before' | 'after'
  unitSpace?: boolean
}

/**
 * Formats a number with optional unit and decimal places. Mirrors the
 * helper used by the sibling CPM calculator so the two cost-metric tools
 * render figures identically.
 */
function formatNumberWithOptions(value: number, options: NumberFormatOptions = {}): string {
  const { decimals = 0, unit, unitPosition = 'after', unitSpace = true } = options
  const formattedValue = formatNumber(value, decimals)

  if (!unit) return formattedValue

  const space = unitSpace ? ' ' : ''
  return unitPosition === 'before'
    ? `${unit}${space}${formattedValue}`
    : `${formattedValue}${space}${unit}`
}

interface NumberInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  isDecimal?: boolean
  placeholder: string
  label: string
  tooltip: string
  onIncrement: () => void
  onDecrement: () => void
}

const FAQ_ITEMS = [
  {
    question: "What is CPV (cost per view)?",
    answer: "CPV (Cost Per View) is a video advertising metric that measures how much you pay for each view of your ad. It is the standard efficiency metric for view-based buying on platforms like YouTube, Meta, and TikTok, where you are charged for engaged video views rather than raw impressions (CPM) or clicks (CPC). A lower CPV means you are buying attention more cheaply — but only relative to the same view definition (see below)."
  },
  {
    question: "How do you calculate CPV?",
    answer: "CPV is your total ad spend divided by the number of views: CPV = Total Ad Spend / Total Views. For example, $500 spent for 10,000 views is a $0.05 CPV. This calculator also derives your view rate (views ÷ impressions) when you enter impressions, and your cost per ThruPlay when you enter ThruPlays, so you can see view efficiency from a few angles at once."
  },
  {
    question: "What counts as a 'view' — and why does it make CPV hard to compare?",
    answer: "Each platform defines a view differently, so CPV is only comparable within the same platform and definition. YouTube TrueView in-stream counts a view at 30 seconds (or the full ad if shorter, or an interaction). Meta's ThruPlay optimizes for completions or at least 15 seconds, while its older video-view metric counted 3 seconds. TikTok reports views at impression but also 2-second and 6-second focused views. Always pair a CPV with the view definition behind it; a $0.02 '2-second view' CPV and a $0.20 'completed view' CPV can describe the same campaign."
  },
  {
    question: "What is a good CPV?",
    answer: "It depends on platform, view definition, format, audience, and geography. As 2025–2026 ranges: YouTube TrueView in-stream averages $0.01–$0.03 globally for broad consumer targeting, but $0.10–$0.30 for narrow B2B and high-CPM verticals; YouTube Shorts runs $0.10–$0.30 (scarcer vertical inventory); Meta ThruPlay commonly lands $0.01–$0.04; TikTok in-feed sits around $0.02–$0.05 in the US — mechanically cheap because of its 2-second view definition. Because the underlying definitions differ, compare your CPV against your own history on the same platform and objective rather than across platforms."
  },
  {
    question: "How is CPV different from CPM and cost per ThruPlay?",
    answer: "All three measure media efficiency at different points. CPM is cost per 1,000 impressions (paying for exposure, regardless of whether anyone watched). CPV is cost per single view (paying for engaged views). Cost per ThruPlay is a stricter, Meta-specific CPV variant that only counts longer/completed plays, so it is always higher than a loose video-view CPV. Reading them together — CPM for reach cost, CPV for view cost, cost per ThruPlay for completed-view cost — tells you where in the funnel your spend is going."
  },
  {
    question: "How do I calculate cost per completed view (CPCV)?",
    answer: "CPCV = Total Ad Spend ÷ Views that reached 100% completion — the same spend divided by a much stricter view count: only full completions make it into the denominator. Because far fewer viewers complete than cross a 2–30 second view threshold, that denominator shrinks and CPCV typically runs 3–10x higher than CPV on the same campaign. CPCV is most common on CTV/OTT inventory where completion rates already exceed 95%; use it when full-message exposure is the goal, and CPV when efficient consideration is."
  }
]

/**
 * Platform CPV ranges, kept in sync with the CPV glossary entry's
 * attributed `segmentBenchmarks` in `data/glossary.yaml` and the CPV
 * calculator registry FAQ in `src/lib/resources/data/calculators/index.ts`
 * (2025–2026 set: Store Growers 2026; OwlClaw 2026; InBeat 2025; Sensor
 * Tower Q1 2025; Sprout Social 2025) — this page renders both surfaces,
 * so they must agree. `r3540594588` CPV is still not standardized across
 * platforms (each counts a "view" differently — see the FAQ), so these
 * give a sense of scale, not a precise target.
 */
const PLATFORM_BENCHMARKS = [
  { platform: 'YouTube TrueView in-stream (broad consumer)', cpv: { low: 0.01, average: 0.026, high: 0.03 }, note: '~30s / full-view definition' },
  { platform: 'YouTube TrueView in-stream (B2B / high-CPM verticals)', cpv: { low: 0.1, average: 0.18, high: 0.3 }, note: '~30s / full-view definition; narrow targeting pulls CPV 5–10x higher' },
  { platform: 'YouTube Shorts ads', cpv: { low: 0.1, average: 0.2, high: 0.3 }, note: 'scarcer vertical inventory — CPV runs higher, CPM roughly half of in-stream' },
  { platform: 'Meta (cost per ThruPlay)', cpv: { low: 0.01, average: 0.02, high: 0.04 }, note: '15s / completion definition' },
  { platform: 'TikTok in-feed (US)', cpv: { low: 0.02, average: 0.021, high: 0.05 }, note: '2s view definition — mechanically cheap, not comparable to YouTube’s 30s view' },
]

/**
 * Formats a benchmark CPV for display: two decimals for round-cent values,
 * three where the sourced median needs sub-cent precision (e.g. $0.026).
 */
const formatBenchmarkCpv = (value: number): string =>
  formatNumberWithOptions(value, { decimals: Math.abs(value * 100 - Math.round(value * 100)) > 1e-9 ? 3 : 2 })

// Custom input component with better number formatting
const NumberInput = ({
  id,
  value,
  onChange,
  isDecimal = false,
  placeholder,
  label,
  tooltip,
  onIncrement,
  onDecrement
}: NumberInputProps): JSX.Element => (
  <div>
    <div className="flex items-center justify-between">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <Tooltip content={tooltip}>
        <RiInformationLine className="h-4 w-4 text-gray-400" />
      </Tooltip>
    </div>
    <div className="relative mt-1 flex rounded-md shadow-sm">
      {isDecimal && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-gray-500 sm:text-sm">$</span>
        </div>
      )}
      <input
        id={id}
        type="text"
        inputMode={isDecimal ? "decimal" : "numeric"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 ${isDecimal ? 'pl-7' : ''}`}
        placeholder={placeholder}
      />
      <div className="absolute inset-y-0 right-0 flex -space-x-px">
        <button
          type="button"
          onClick={onIncrement}
          aria-label={`Increase ${label}`}
          className="rounded-r-md border border-gray-300 bg-gray-50 px-2 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onDecrement}
          aria-label={`Decrease ${label}`}
          className="rounded-r-md border border-gray-300 bg-gray-50 px-2 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
        >
          ▼
        </button>
      </div>
    </div>
  </div>
)

// Analytics configuration
const ANALYTICS_CONFIG: AnalyticsConfig = {
  enabled: true,
  category: 'calculators',
  action: 'email_capture',
  label: 'cpv_calculator_results_email_signup',
  elementId: 'cpv-calculator-email-form',
  pageLocation: 'CPV Calculator'
}

// CRM configuration
const CRM_CONFIG: CRMConfig = {
  tags: ['calculator_results', 'cpv'],
  source: 'cpv_calculator',
  customFields: {
    calculator_type: 'CPV',
    signup_location: 'calculator_results'
  },
  options: {
    addToList: true,
    updateExisting: true,
    retryOnFailure: true,
    retryAttempts: 3
  }
}

/**
 * Props for the CPV calculator. The calculator route passes `faqItems`
 * straight from the registry record's `faqs` — the same array
 * `WithResourceInstanceStructuredData` emits as `FAQPage` JSON-LD — so the
 * VISIBLE FAQ and the structured data share a single source and can't
 * drift apart (`r3540802940`). The built-in `FAQ_ITEMS` are only the
 * standalone-render fallback.
 */
export interface CPVCalculatorProps {
  /** FAQ entries to render. Passed from the registry record so visible
   *  copy matches the JSON-LD. */
  faqItems?: { question: string; answer: string }[]
}

export function CPVCalculator({
  faqItems = FAQ_ITEMS.map(item => ({ ...item })),
}: CPVCalculatorProps = {}): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    adSpend: 500,
    views: 10000,
    impressions: undefined,
    thruplays: undefined
  })

  const [displayValues, setDisplayValues] = useState({
    adSpend: '500.00',
    views: '10,000',
    impressions: '',
    thruplays: ''
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  // Debounced interact tracker (no input values; lean payload)
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'cpv-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const emailTemplate = useMemo(() => {
    const baseTemplate: EmailTemplateConfig = {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: '',
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702'
        },
        custom: {
          calculator_type: 'CPV',
          // Build the payload from the input SNAPSHOT captured in `results`, not
          // the live form — otherwise editing an input after calculating (but
          // before submitting the email) would email/CRM-store metrics paired
          // with mismatched inputs.
          calculator_results: results ? {
            metrics: results,
            inputs: {
              adSpend: results.inputs.adSpend,
              views: results.inputs.views,
              impressions: results.inputs.impressions,
              thruplays: results.inputs.thruplays
            }
          } : null
        }
      }
    }

    return baseTemplate
  }, [results])

  const handleInputChange = (field: keyof CalculatorInputs, value: string): void => {
    const isDecimal = field === 'adSpend'
    const formattedValue = formatNumberInput(value, isDecimal)

    setDisplayValues(prev => ({
      ...prev,
      [field]: formattedValue
    }))

    const numericValue = parseInputNumber(formattedValue)
    setInputs(prev => ({
      ...prev,
      [field]: numericValue || undefined
    }))
    trackInteract('input_change')
  }

  const handleStepChange = (field: keyof CalculatorInputs, increment: boolean, step: number): void => {
    const currentValue = inputs[field] ?? 0
    const newValue = Math.max(0, increment ? currentValue + step : currentValue - step)

    setInputs(prev => ({
      ...prev,
      [field]: newValue
    }))

    const isDecimal = field === 'adSpend'
    setDisplayValues(prev => ({
      ...prev,
      [field]: formatNumberInput(newValue.toString(), isDecimal)
    }))
    trackInteract('input_change')
  }

  const handleCalculate = (): void => {
    if (inputs.adSpend > 0 && inputs.views > 0) {
      const newResults = calculateMetrics(inputs)
      setResults(newResults)
      sendResourceEvent('interact', {
        resourceType: 'calculator',
        resourceSlug: 'cpv-calculator',
        engagementMs: getDeltaMs(),
        params: { interaction_type: 'calculate' }
      })
      sendResourceEvent('complete', {
        resourceType: 'calculator',
        resourceSlug: 'cpv-calculator',
        engagementMs: getTotalMs(),
        params: { }
      })
    } else {
      // Required inputs can't produce a CPV (zeroed/cleared ad spend or views).
      // Clear any prior result so a stale CPV/view-rate card + email capture
      // don't linger next to inputs that no longer support them.
      setResults(null)
    }
  }

  const formatDisplayNumber = (value: number | undefined, options: NumberFormatOptions = {}): string => {
    if (value === undefined) return ''
    return formatNumberWithOptions(value, options)
  }

  return (
    <div className="mx-auto max-w-4xl" role="region" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            CPV Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate cost per view (CPV) — plus view rate, CPM, and cost per ThruPlay
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column - Required Metrics */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Required Metrics</h3>

            <NumberInput
              id="ad-spend"
              value={displayValues.adSpend}
              onChange={(value: string) => handleInputChange('adSpend', value)}
              isDecimal={true}
              placeholder="Enter amount"
              label="Total Ad Spend"
              tooltip="The total amount spent on your video campaign"
              onIncrement={() => handleStepChange('adSpend', true, 100)}
              onDecrement={() => handleStepChange('adSpend', false, 100)}
            />

            <NumberInput
              id="views"
              value={displayValues.views}
              onChange={(value: string) => handleInputChange('views', value)}
              placeholder="Enter views"
              label="Total Views"
              tooltip="The total number of paid video views your campaign generated (use your platform's view definition)"
              onIncrement={() => handleStepChange('views', true, 1000)}
              onDecrement={() => handleStepChange('views', false, 1000)}
            />
          </div>

          {/* Right Column - Optional context */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Optional Context
            </h3>

            <NumberInput
              id="impressions"
              value={displayValues.impressions}
              onChange={(value: string) => handleInputChange('impressions', value)}
              placeholder="Enter impressions"
              label="Total Impressions"
              tooltip="Add impressions to derive your view rate (views ÷ impressions) and CPM"
              onIncrement={() => handleStepChange('impressions', true, 1000)}
              onDecrement={() => handleStepChange('impressions', false, 1000)}
            />

            <NumberInput
              id="thruplays"
              value={displayValues.thruplays}
              onChange={(value: string) => handleInputChange('thruplays', value)}
              placeholder="Enter ThruPlays"
              label="ThruPlays (Meta)"
              tooltip="Add Meta ThruPlays to derive your cost per ThruPlay (a stricter, completion-based CPV)"
              onIncrement={() => handleStepChange('thruplays', true, 100)}
              onDecrement={() => handleStepChange('thruplays', false, 100)}
            />
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate CPV and related metrics"
          >
            Calculate Metrics
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8"
          >
            {/* Combined Results Card */}
            <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Cost & View Efficiency
                </h3>
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPV</dt>
                    <dd className="mt-2 text-3xl font-semibold text-indigo-600 dark:text-indigo-400">
                      ${formatDisplayNumber(results.cpv, { decimals: 3 })}
                    </dd>
                  </div>
                  {results.viewRate !== undefined && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">View Rate</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {formatDisplayNumber(results.viewRate, { decimals: 1 })}%
                      </dd>
                      {results.viewRateClamped && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          Views exceed impressions — capped at 100%. Check your inputs.
                        </p>
                      )}
                    </div>
                  )}
                  {results.cpm !== undefined && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPM</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        ${formatDisplayNumber(results.cpm, { decimals: 2 })}
                      </dd>
                    </div>
                  )}
                  {results.costPerThruplay !== undefined && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cost / ThruPlay</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        ${formatDisplayNumber(results.costPerThruplay, { decimals: 3 })}
                      </dd>
                      {results.thruplaysExceedViews && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          ThruPlays exceed views — a ThruPlay is a stricter view, so it can&rsquo;t outnumber views. Check your inputs.
                        </p>
                      )}
                    </div>
                  )}
                </dl>
              </div>

              {/* Platform Benchmarks */}
              <div className="pt-8 border-t border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Indicative Platform CPV Ranges
                </h3>

                <div className="grid gap-4 sm:grid-cols-3">
                  {PLATFORM_BENCHMARKS.map((platform) => (
                    <div
                      key={platform.platform}
                      className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <dt className="text-sm font-medium text-gray-900 dark:text-white">
                          {platform.platform}
                        </dt>
                        <Tooltip content={`${platform.note}. CPV is not comparable across platforms with different view definitions.`}>
                          <RiInformationLine className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </Tooltip>
                      </div>
                      <dd className="text-sm text-gray-600 dark:text-gray-400">
                        Average: ${formatBenchmarkCpv(platform.cpv.average)}
                        <br />
                        Range: ${formatBenchmarkCpv(platform.cpv.low)} – ${formatBenchmarkCpv(platform.cpv.high)}
                      </dd>
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                  <p>* Indicative 2025–2026 ranges. Each platform counts a &ldquo;view&rdquo; differently (see the FAQ), so CPV is not directly comparable across platforms — benchmark against your own results on the same platform and objective. Actual CPV varies with format, audience, geography, and seasonality.</p>
                </div>
              </div>
            </div>

            {/* Embedded Email Capture */}
            <EmbeddedEmailCapture
              preset="inline"
              content={{
                title: {
                  text: "Get Your Detailed Analysis",
                  className: 'text-2xl font-semibold'
                },
                description: {
                  text: "Receive a comprehensive breakdown of your CPV, view rate, and cost per ThruPlay, with optimization recommendations.",
                  className: 'text-gray-600 dark:text-gray-400'
                },
                button: {
                  text: "Get Analysis",
                  loadingText: "Preparing Analysis..."
                },
                input: {
                  label: "Work Email",
                  placeholder: "Enter your work email"
                },
                messages: {
                  success: "Analysis sent!",
                  error: "There was an error sending your analysis. Please try again."
                }
              }}
              template={emailTemplate}
              analytics={ANALYTICS_CONFIG}
              crm={CRM_CONFIG}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section - always visible */}
      <div className="mt-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ
          items={faqItems.map(item => ({ ...item }))}
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "xl" }}
          className="w-full"
        />
      </div>
    </div>
  )
}
